import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useCallback, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { useBackButton } from '../lib/navigation'

const DISMISS_AT = 90

function sheetOutDuration(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--duration-sheet-out')
  return Number.parseFloat(raw) || 200
}

export function SheetShell({
  title,
  subtitle,
  onClose,
  matte,
  footer,
  children,
}: {
  title: string
  subtitle?: string
  onClose: () => void
  matte?: boolean
  footer?: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(true)
  const [dragged, setDragged] = useState(0)
  const start = useRef<number | undefined>(undefined)
  const content = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    setOpen(false)
    window.setTimeout(onClose, sheetOutDuration())
  }, [onClose])

  useBackButton(close)

  function onPointerDown(event: React.PointerEvent) {
    start.current = event.clientY
  }

  function onPointerMove(event: React.PointerEvent) {
    if (start.current === undefined) return
    setDragged(Math.max(0, event.clientY - start.current))
  }

  function onPointerUp() {
    if (start.current === undefined) return
    const distance = dragged
    start.current = undefined
    setDragged(0)
    if (distance > DISMISS_AT) close()
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogPrimitive.Portal forceMount>
        <DialogPrimitive.Overlay
          className={`fixed inset-0 z-40 ${matte ? 'bg-overlay-strong' : 'bg-overlay'} ${
            open ? 'animate-fade-in' : 'animate-fade-out'
          }`}
        />
        <DialogPrimitive.Content
          ref={content}
          tabIndex={-1}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            content.current?.focus({ preventScroll: true })
          }}
          className={`fixed inset-x-0 bottom-0 z-40 flex max-h-[86vh] flex-col border-t shadow-sheet outline-hidden ${
            footer ? '' : 'pb-[calc(10px+(var(--spacing-safe-bottom)))]'
          } ${open ? 'animate-sheet-in' : 'animate-sheet-out'} ${
            matte
              ? 'rounded-t-sheet-matte border-hairline-soft bg-sheet-matte'
              : 'rounded-t-sheet border-sheet bg-sheet backdrop-blur-3 backdrop-saturate-(--glass-saturate)'
          }`}
          style={
            dragged > 0
              ? { transform: `translateY(${dragged}px)`, transition: 'none' }
              : { transition: 'transform var(--duration-sheet-out) var(--ease-sheet)' }
          }
        >
          <div
            className="flex-none cursor-grab touch-none active:cursor-grabbing"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <div className="flex justify-center pb-[3px] pt-[9px]">
              <span
                className={`h-1 w-9 rounded-grabber transition-colors duration-mark ${
                  dragged > 0 ? 'bg-grabber-held' : 'bg-grabber'
                }`}
              />
            </div>
            <DialogPrimitive.Title className="px-[14px] pt-[6px] text-sheet-title">
              {title}
            </DialogPrimitive.Title>
            {subtitle ? (
              <DialogPrimitive.Description className="px-[14px] pb-[10px] pt-[3px] text-hint text-text-muted">
                {subtitle}
              </DialogPrimitive.Description>
            ) : (
              <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
            )}
          </div>

          <div
            className={`min-h-0 flex-1 overflow-y-auto px-[14px] ${
              footer
                ? 'pb-2 [mask-image:linear-gradient(to_bottom,#000_calc(100%-24px),transparent)]'
                : 'pb-1'
            }`}
          >
            {children}
          </div>
          {footer ? (
            <div className="flex-none px-[14px] pb-[calc(10px+(var(--spacing-safe-bottom)))] pt-[10px]">
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
